import './SummaryInstallationPage.scss';
import { useTranslation } from '../../../i18n/i18n';
import InfoCard from '../../../components/installation/summary/InfoCard';
import OtherFieldsCard from '../../../components/installation/summary/OtherFieldsCard';
import FilesCard from '../../../components/installation/summary/FilesCard';
import { message } from 'antd';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { installationApi } from '../../../api/installation.api';
import { deviceApi } from '../../../api/device';
import { type Installation } from '../../../gen/proto/api/gateway/v1/installation_pb';
import type { JsonObject } from '@bufbuild/protobuf';
import { type Device, DeviceType } from '../../../gen/proto/api/gateway/v1/device_pb';
import {
  installationStateLabel,
  siphonDiameterLabel,
  siphonMaterialLabel,
  siphonTypeLabel,
} from '../../../utils/mapTranslationEnums';
import { installationStateApi } from '../../../api/installation-state.api';
import { locationApi } from '../../../api/location.api';
import { filesApi } from '../../../api/files.api';
import { type File as InstallationFile } from '../../../gen/proto/api/gateway/v1/file_pb';
import CommentsCard from '../../../components/installation/summary/CommentsCard';

const valueEmpty = '-';

const SummaryInstallationPage = () => {
  const { installationUuid } = useParams<{ installationUuid: string }>();
  const { t } = useTranslation('installation-location', {
    keyPrefix: 'installationLocationDetail.installation.summary',
  });

  const [messageApi, contextHolder] = message.useMessage();
  const [installation, setInstallation] = useState<Installation | null>(null);
  const [otherFields, setOtherFields] = useState<{ title: string; value: string }[]>([]);
  const [comment, setComment] = useState<string>('');
  const [devices, setDevices] = useState<Device[]>([]);
  const [status, setStatus] = useState<string>('');
  const [statusColor, setStatusColor] = useState<string>('');
  const [files, setFiles] = useState<InstallationFile[]>([]);
  const [adminUuid, setAdminUuid] = useState<string | undefined>(undefined);

  const refreshInstallation = async () => {
    if (!installationUuid) return;
    try {
      const fetched = await installationApi.getByUuid(installationUuid);
      setInstallation(fetched);
      if (fetched.installationStateUuid) {
        const state = await installationStateApi.getByUuid(fetched.installationStateUuid);
        setStatus(installationStateLabel(state));
        setStatusColor(state?.color ?? '');
      }
    } catch (error) {
      console.error('Failed to refresh installation:', error);
    }
  };

  useEffect(() => {
    if (!installationUuid) return;
    const fetchData = async () => {
      try {
        const fetched = await installationApi.getByUuid(installationUuid);
        setInstallation(fetched);
        const info = fetched.otherInfo ?? {};
        setOtherFields(Object.entries(info).map(([key, val]) => ({ title: key, value: String(val) })));
        setComment(fetched.comment ?? '');

        if (fetched.installationStateUuid) {
          const state = await installationStateApi.getByUuid(fetched.installationStateUuid);
          setStatus(installationStateLabel(state));
          setStatusColor(state?.color ?? '');
        }
      } catch (error) {
        messageApi.error(t('common:errors.generic'));
      }

      try {
        const devices = await deviceApi.getDeviceByInstallation(installationUuid);
        setDevices(devices);
      } catch (error) {
        messageApi.error(t('common:errors.generic'));
      }

      try {
        const filesResponse = await filesApi.getFilesByUuidInstallation(installationUuid);
        setFiles(filesResponse);
      } catch (error) {
        messageApi.error(t('common:errors.generic'));
      }
    };
    fetchData();
  }, [installationUuid, messageApi, t]);

  useEffect(() => {
    if (!installation?.locationUuid) return;
    const fetchLocationAdmin = async () => {
      try {
        const loc = await locationApi.getByUuid(installation.locationUuid);
        setAdminUuid(loc.adminUuid || undefined);
      } catch (error) {
        setAdminUuid(undefined);
      }
    };
    fetchLocationAdmin();
  }, [installation?.locationUuid]);

  const getAllFiles = async () => {
    if (!installationUuid) return;
    const filesResponse = await filesApi.getFilesByUuidInstallation(installationUuid);
    setFiles(filesResponse);
  };

  const handleFileDeleted = (fileUuid: string) => {
    setFiles((prev) => prev.filter((file) => file.fileUuid !== fileUuid));
  };

  const refreshDevices = async () => {
    if (!installationUuid) return;
    const devicesResponse = await deviceApi.getDeviceByInstallation(installationUuid);
    setDevices(devicesResponse);
  };

  const handleSaveInstallation = async (overrides: {
    siphonCollapseLimitPercentage?: number;
    otherInfo?: JsonObject;
    comment?: string;
  }) => {
    if (!installation || !installationUuid) return;
    const siphonLimit =
      'siphonCollapseLimitPercentage' in overrides
        ? overrides.siphonCollapseLimitPercentage!
        : installation.siphonCollapseLimitPercentage;
    const updatedOtherInfo = 'otherInfo' in overrides ? overrides.otherInfo : installation.otherInfo;
    const updatedComment = 'comment' in overrides ? overrides.comment! : installation.comment;
    try {
      await installationApi.update(
        installationUuid,
        installation.name,
        installation.erpId,
        installation.locationUuid,
        installation.siphonType,
        installation.siphonMaterial,
        installation.siphonDiameterMm,
        installation.siphonEffluentSiphonUuid || undefined,
        siphonLimit,
        updatedOtherInfo,
        updatedComment,
      );
      messageApi.success(t('update'));
    } catch {
      messageApi.error(t('common:errors.generic'));
      throw new Error();
    } finally {
    }
    setInstallation((prev) => (prev ? ({ ...prev, ...overrides } as Installation) : prev));
    if ('otherInfo' in overrides) {
      const info = overrides.otherInfo ?? {};
      setOtherFields(Object.entries(info).map(([key, val]) => ({ title: key, value: String(val) })));
    }
    if ('comment' in overrides) {
      setComment(overrides.comment ?? '');
    }
  };

  const handleSaveSiphonLimit = async (newLimit: number) => {
    await handleSaveInstallation({ siphonCollapseLimitPercentage: newLimit });
  };

  const handleSaveOtherFields = async (fields: { title: string; value: string }[]) => {
    const otherInfo: JsonObject = Object.fromEntries(fields.map(({ title, value }) => [title, value]));
    await handleSaveInstallation({ otherInfo });
  };

  const handleSaveComments = async (newComment: string) => {
    await handleSaveInstallation({ comment: newComment });
  };

  let sensorCount = 0;
  const device_data = [
    ...devices.map((device) => {
      if (device.type === DeviceType.DRIVER) {
        return { id: device.uuid, title: t('devices.driver'), data: device.name || valueEmpty };
      }
      sensorCount += 1;
      return {
        id: device.uuid,
        title: t('devices.sensor', { number: sensorCount }),
        data: device.name || valueEmpty,
      };
    }),
    { id: 'state', title: t('devices.state'), data: status || valueEmpty, color: statusColor },
  ];

  const siphon_data = [
    {
      id: 'type',
      title: t('siphon.type'),
      data: installation ? siphonTypeLabel(installation.siphonType) : valueEmpty,
    },
    {
      id: 'material',
      title: t('siphon.material'),
      data: installation ? siphonMaterialLabel(installation.siphonMaterial) : valueEmpty,
    },
    {
      id: 'diameter',
      title: t('siphon.diameter'),
      data: installation ? siphonDiameterLabel(installation.siphonDiameterMm) : valueEmpty,
    },
    {
      id: 'effluent',
      title: t('siphon.effluent'),
      data: installation?.siphonEffluentSiphonUuid || valueEmpty,
    },
    {
      id: 'limit',
      title: t('siphon.limit'),
      data: installation ? String(installation.siphonCollapseLimitPercentage) : valueEmpty,
      onSave: handleSaveSiphonLimit,
    },
  ];

  return (
    <div className={'SummaryInstallationPage'}>
      {contextHolder}
      <InfoCard data={device_data} title={t('devices.title')} isDevices={true} onSuccess={refreshDevices} />
      <CommentsCard comment={comment} onSave={handleSaveComments} />
      <InfoCard
        data={siphon_data}
        title={t('siphon.title')}
        isDevices={false}
        installationUuid={installationUuid}
        onSuccess={refreshInstallation}
        adminUuid={adminUuid}
      />
      <FilesCard
        files={files}
        installationUuid={installationUuid!}
        onFileAdded={getAllFiles}
        onFileDeleted={handleFileDeleted}
      />
      <OtherFieldsCard fields={otherFields} onSave={handleSaveOtherFields} adminUuid={adminUuid} />
    </div>
  );
};

export default SummaryInstallationPage;
